import { useState } from "react";
import { Search, MapPin, Navigation, X } from "lucide-react";
import { ClientListToggle, PickMark, ClientListBar } from "@/components/ClientList";
import { CATEGORY_META } from "@/data/mapConfig";
import { DirectionsIcon, ShareNearestButton, ShareProviderButton } from "@/components/CardActions";

function fmtMiles(m) {
  if (m == null) return "";
  return m < 10 ? `${m.toFixed(1)} mi` : `${Math.round(m)} mi`;
}

export default function Sidebar({ locations, selected, onSelect, search, setSearch, nearestMode, zipInfo, stateToggle }) {
  const [listMode, setListMode] = useState(false);
  const [picked, setPicked] = useState([]);
  const togglePick = (loc) =>
    setPicked((p) => (p.some((x) => x.id === loc.id) ? p.filter((x) => x.id !== loc.id) : [...p, loc]));
  const order = ["pain_management", "orthopedics", "chiropractic", "diagnostic_imaging", "neurology_tbi", "telemed", "other_services", "pharmacy"];

  const Card = ({ loc }) => {
    const meta = CATEGORY_META[loc.category];
    const active = selected?.id === loc.id;
    const isPhysical = loc.pinned !== false && !!loc.city && loc.note !== "Coming Soon";
    const isPicked = picked.some((x) => x.id === loc.id);
    const pickable = listMode && isPhysical;
    const handle = () => (pickable ? togglePick(loc) : onSelect(loc));
    return (
      <div
        role="button"
        tabIndex={0}
        data-testid={`provider-card-${loc.id}`}
        onClick={handle}
        onKeyDown={(e) => e.key === "Enter" && handle()}
        className={`w-full text-left flex cursor-pointer gap-3 p-3 rounded-lg border transition-all duration-200 ${
          isPicked ? "bg-orange-500/15 border-orange-400/60" : active ? "bg-[#22305f] border-blue-400/60 -translate-y-0.5" : "bg-[#1A275B] border-white/5 hover:border-white/25 hover:-translate-y-0.5"
        }`}
      >
        <div
          className="w-7 h-7 shrink-0 rounded-md flex items-center justify-center text-white text-xs font-bold font-heading mt-0.5"
          style={{ background: meta.color }}
        >
          {loc.number}
        </div>
        <div className="min-w-0 flex-1">
          {loc.company ? (
            <>
              <p className="text-sm font-semibold text-white truncate">{loc.company}</p>
              <p className="text-xs text-blue-300 font-medium leading-snug">{loc.name}</p>
            </>
          ) : (
            <p className="text-sm font-semibold text-white truncate">{loc.name}</p>
          )}
          <p className="text-xs text-slate-400 leading-snug">{loc.address}</p>
          {loc.pinned !== false && loc.city ? (
            <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
              <MapPin className="w-3 h-3" /> {loc.city}, {loc.state} {loc.zip}
            </p>
          ) : null}
          {loc.note && (
            <span
              className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 text-white shadow-sm"
              style={{ background: meta.color }}
            >
              <Navigation className="w-2.5 h-2.5" /> {loc.note}
            </span>
          )}
        </div>
        {(isPhysical || (nearestMode && loc._miles != null)) && (
          <span className="shrink-0 self-start flex flex-col items-end gap-1">
            {nearestMode && locations[0]?.id === loc.id && (
              <span data-testid="closest-badge" className="text-[10px] font-bold uppercase tracking-wide text-[#0B132B] bg-emerald-300 rounded-full px-2 py-0.5">Closest</span>
            )}
            {nearestMode && loc._miles != null && (
              <span
                data-testid={`miles-badge-${loc.id}`}
                className="text-[11px] font-bold text-emerald-300 bg-emerald-500/15 border border-emerald-400/30 rounded-full px-2 py-1 whitespace-nowrap"
              >
                {fmtMiles(loc._miles)}
              </span>
            )}
            {pickable && <PickMark picked={isPicked} id={loc.id} />}
            {isPhysical && !listMode && (
              <span className="flex gap-1">
                <DirectionsIcon loc={loc} />
                <ShareProviderButton loc={loc} />
              </span>
            )}
          </span>
        )}
      </div>
    );
  };

  const groups = order
    .map((cat) => ({ cat, items: locations.filter((l) => l.category === cat).sort((a, b) => (a.number || 0) - (b.number || 0)) }))
    .filter((g) => g.items.length > 0);

  return (
    <aside
      data-testid="directory-panel"
      className="w-full sm:w-[380px] shrink-0 bg-[#111D4A] border-r border-white/10 flex flex-col h-full"
    >
      <div className="p-4 border-b border-white/10">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="directory-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search client ZIP, practice, or city..."
            className="w-full bg-[#1A275B] border border-white/15 rounded-lg pl-9 pr-9 py-2.5 text-sm text-white placeholder:text-slate-400 focus:ring-2 focus:ring-blue-400 focus:outline-none transition-all"
          />
          {search && (
            <button data-testid="directory-search-clear" onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="sm:hidden mt-2.5">{stateToggle}</div>
        <ClientListToggle active={listMode} onToggle={() => setListMode((v) => !v)} />
        {nearestMode && (
          <div
            data-testid="nearest-banner"
            className="mt-3 flex items-center gap-2 text-xs text-emerald-200 bg-emerald-500/10 border border-emerald-400/25 rounded-lg px-3 py-2"
          >
            <Navigation className="w-3.5 h-3.5 shrink-0" />
            <span>
              Nearest locations to <span className="font-bold">{zipInfo?.zip}</span>
              {zipInfo?.city ? ` · ${zipInfo.city}, ${zipInfo.state}` : ""}
            </span>
            <ShareNearestButton zipInfo={zipInfo} locations={locations} />
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto dfw-scroll p-3 space-y-2">
        {locations.length === 0 && (
          <p className="text-center text-slate-400 text-sm py-10" data-testid="no-results">
            No locations match your search.
          </p>
        )}

        {nearestMode
          ? locations.map((loc) => <Card key={loc.id} loc={loc} />)
          : groups.map(({ cat, items }) => {
              const meta = CATEGORY_META[cat];
              return (
                <div key={cat} className="pb-3">
                  <div className="flex items-center gap-2 px-1 mb-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: meta.color }} />
                    <h3 className="font-heading text-xs font-bold uppercase tracking-[0.15em] text-slate-300">{meta.label}</h3>
                    <span className="text-[11px] text-slate-500">{items.length}</span>
                  </div>
                  <div className="space-y-2">
                    {items.map((loc) => <Card key={loc.id} loc={loc} />)}
                  </div>
                </div>
              );
            })}
      </div>
      <ClientListBar picked={picked} onClear={() => setPicked([])} />
    </aside>
  );
}
